import * as diagnostics from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';
import { formatCurrency } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';
import { FlowChartGraph } from '@/utils/graphology/FlowChartGraph';

import {
  EntityGraphAttributes,
  EntityGraphBuilderFn,
  EntityGraphEdgeAttributes,
  EntityGraphNodeAttributes,
} from '../types';
import { createEdge } from '../utils/edges';
import { normalizeById } from '../utils/normalize';
import { createBeneficiaryNodeFromClientProfile } from './beneficiaries';
import { entitySubtypeToNodeCategorization } from './constants';
import {
  addGrantors,
  createGrantorNodeFromClientProfile,
  getGrantorOrIndividualNodeId,
} from './grantors';
import { OwnershipEdge } from './ownershipEdge';
import { entitySubtypeToTile, individualBeneficiaryTile } from './tiles';

export const createOwnershipEntityGraph: EntityGraphBuilderFn = (props) => {
  const { entities, possibleBeneficiaries, possibleGrantors, orientation } =
    props;
  const byId = normalizeById(props);
  const graph = new FlowChartGraph<
    EntityGraphNodeAttributes,
    EntityGraphEdgeAttributes,
    EntityGraphAttributes
  >();

  graph.setAttribute('byId', byId);
  graph.setAttribute('orientation', orientation);

  possibleBeneficiaries.clients.forEach((cp) => {
    if (!cp.ownedOwnershipStakes?.length) return;
    // Ownership views use one tile per person. Keep grantor IDs for existing
    // ownership connections and saved layouts.
    if (byId.individualGrantorsById[cp.id]) return;
    const node = createBeneficiaryNodeFromClientProfile(graph, cp);
    graph.addNodeSafe(node.node.id, node);
  });

  possibleGrantors.forEach((cp) => {
    if (!cp.ownedOwnershipStakes?.length) return;
    const node = createGrantorNodeFromClientProfile(graph, cp)!;
    graph.addNodeSafe(node.node.id, node);
  });

  // Ensure we add all non business entities to the graph first,
  // so we can utilize looking them when building the business entities
  const sortedEntities = entities
    .slice()
    .sort((e) => (e.subtype.__typename?.includes('BusinessEntity') ? 1 : -1));

  sortedEntities.forEach(function addEntityToGraph(entity) {
    const supportedEntityTypename = entity.subtype.__typename!;
    const entityTile = entitySubtypeToTile[supportedEntityTypename](entity);
    graph.addNodeSafe(entityTile.id, {
      node: entityTile,
      // Guarantee the discriminator so the design-mode tile recognizes an
      // editable entity even if the payload omitted `__typename`.
      data: { ...entity, __typename: 'Entity' },
      categorizationType:
        entitySubtypeToNodeCategorization[supportedEntityTypename],
    });

    switch (entity.subtype.__typename) {
      case 'GRATTrust':
      case 'SLATTrust': {
        const { grantor } = entity.subtype;
        addGrantors(graph, entity.id, [grantor]);
        return;
      }

      case 'RetirementPersonalAccount':
      case 'QualifiedTuitionPersonalAccount':
      case 'CustodialPersonalAccount':
      case 'IndividualPersonalAccount': {
        const { owner } = entity.subtype;
        addGrantors(graph, entity.id, [owner]);
        return;
      }

      case 'JointPersonalAccount': {
        const { owners } = entity.subtype;
        addGrantors(graph, entity.id, owners);
        return;
      }

      case 'InsurancePersonalAccount': {
        const { owners } = entity.subtype;
        addGrantors(graph, entity.id, owners);
        return;
      }

      case 'DonorAdvisedFund':
      case 'PrivateFoundation': {
        const { donors } = entity.subtype;
        addGrantors(graph, entity.id, donors);
        return;
      }

      case 'ILITTrust':
      case 'IrrevocableTrust':
      case 'RevocableTrust':
      case 'QPRTTrust':
      case 'CLTTrust':
      case 'CRTTrust': {
        const { grantors } = entity.subtype;
        addGrantors(graph, entity.id, grantors);
        return;
      }

      case 'SoleProprietorshipBusinessEntity':
      case 'CCorpBusinessEntity':
      case 'LLCBusinessEntity':
      case 'LPBusinessEntity':
      case 'SCorpBusinessEntity':
      case 'GPBusinessEntity': {
        const { ownedByStakes } = entity;
        ownedByStakes?.forEach((stake) => {
          const target = entity.id;
          let source: string | null = null;

          if (stake.owningEntity?.id) {
            source = stake.owningEntity.id;
          } else if (stake.owningIndividual?.id) {
            source = getGrantorOrIndividualNodeId(
              graph,
              stake.owningIndividual.id
            );
          }

          if (!source) {
            throw new Error(
              `Could not find source for ${supportedEntityTypename} ${entity.id}`
            );
          }

          if (stake.owningEntity && !graph.hasNode(source)) {
            const entityToAdd = byId.entitiesById[source];
            if (!entityToAdd) {
              diagnostics.error(
                'ownership-diagram: stake owningEntity missing from entities collection; skipping edge',
                new Error(`No entity for ${source}`),
                {
                  missingEntityId: source,
                  ownedEntityId: entity.id,
                  ownedEntityKind: supportedEntityTypename,
                  stakeId: stake.id,
                }
              );
              return;
            }
            addEntityToGraph(entityToAdd);
          } else if (stake.owningIndividual && !graph.hasNode(source)) {
            // The owner is an individual who's not yet represented in the graph.
            // Attempt to look them up in our normalized maps so we can render a rich tile.

            const { id: individualId, ...individual } = stake.owningIndividual;

            const individualProfile =
              byId.individualGrantorsById[individualId] ??
              byId.individualBeneficiariesById[individualId];

            let nodeToAdd: EntityGraphNodeAttributes | null = null;

            if (individualProfile) {
              // If they are a grantor we want to use the grantor styling, otherwise beneficiary styling.
              if (byId.individualGrantorsById[individualId]) {
                nodeToAdd = createGrantorNodeFromClientProfile(
                  graph,
                  individualProfile
                );
              } else {
                nodeToAdd = createBeneficiaryNodeFromClientProfile(
                  graph,
                  individualProfile
                );
              }
            } else {
              // Fallback: create a beneficiary tile with the individual's name.
              const fallbackNode = individualBeneficiaryTile({
                id: individualId,
                ...individual,
              });

              nodeToAdd = {
                node: fallbackNode,
                data: {
                  id: individualId,
                  ...individual,
                  // `owningIndividual` is a ClientProfile subselection where
                  // `__typename` is optional; guarantee it so downstream
                  // node-kind switches (the design-mode hover toolbar and the
                  // summary panel) recognize this as an editable individual.
                  __typename: 'ClientProfile',
                },
                categorizationType: 'INDIVIDUAL',
              };
            }

            if (nodeToAdd) {
              graph.addNodeSafe(nodeToAdd.node.id, nodeToAdd);
            }
          }

          const percent = stake.ownershipPercentage
            ? `${formatPercent(stake.ownershipPercentage, 2)}%`
            : '';

          const value = `(${formatCurrency(stake.ownedValue, {
            notation: 'compact',
          })})`;

          const data: OwnershipEdge['data'] = {
            edgeLabel: { label: percent, value },
            ownership: {
              entityID: target,
              percentage: stake.ownershipPercentage ?? undefined,
              value: stake.ownedValue,
            },
          };
          const edge = createEdge(graph, {
            source,
            target,
            data,
          });

          graph.addEdgeSafe(source, target, {
            type: 'ownership-percentage',
            edge,
          });
        });
        return;
      }

      default:
        throw new UnreachableError({
          // Type fix for undefined __typename
          case: entity.subtype.__typename as NonNullable<
            typeof entity.subtype.__typename
          >,
          message: `Unhandled entity subtype of ${entity.subtype.__typename}`,
        });
    }
  });

  graph.forEachNode((_id, { node, categorizationType }) => {
    if (categorizationType === 'INDIVIDUAL') {
      delete node.data.lineTwo;
    }
  });

  return graph;
};
